"use client";

import React, { useState, useEffect, useMemo, useRef } from "react";
import ReactECharts from "echarts-for-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { motion, AnimatePresence } from "framer-motion";
import {
  Users,
  Search,
  MapPin,
  ChevronRight,
  ChevronLeft,
  LayoutDashboard,
  Mail,
  Phone,
  Copy,
  Check,
  ExternalLink,
  Globe,
  Shield,
  AlertTriangle
} from "lucide-react";
import { cn } from "@/lib/utils";
import { DualScrollWrapper } from "@/components/directory/DualScrollWrapper";
import { PremiumMultiSelect } from "@/components/directory/PremiumMultiSelect";
import { SortToggle } from "@/components/directory/SortToggle";
import { useRouter } from "next/navigation";
import { useWorkspace } from "@/lib/context/WorkspaceContext";
import { fetchWithAuth } from "@/lib/auth";
import { getViewerTier } from "@/lib/permissions";
import { TourButton } from "@/components/tours/TourButton";
import { useAutoTour } from "@/components/tours/useAutoTour";
import { TOURS } from "@/components/tours/catalog";

type ViewType = "FOOTPRINT" | "CHAPTER_HUB";

const API_BASE = (typeof window !== "undefined") ? "" : (process.env.NEXT_PUBLIC_API_URL || "http://localhost:8001");

// ── Sunburst Palettes ──────────────────────────────────────────────────────
const INNER_PALETTE = [
  "#5E4D66", // Geography Distribution Colors
  "#9B8A97",
  "#C3D4D9",
  "#F9D2D3",
  "#37292A",
  "#4B8893",
  "#A9C4BB",
  "#680000"
];

const ROLE_PALETTE = [
  "#ae232d", "#c85c5c", "#F9D2D3", "#9B8A97", "#b8a0b8",
  "#C3D4D9", "#A9C4BB", "#6b8f8f", "#4B8893", "#468892",
  "#8b3a3a", "#7a9ea8"
];

const ROLE_COLOR_MAP: Record<string, string> = {
  // --- RED SECTION: Active ---
  "Charterholder": "#680000",
  "Professional member": "#8a131a",
  "Other active members": "#ae232d",
  "Candidate L3": "#A9C4BB",
  "Candidate L2": "#4B8893",
  "Candidate L1": "#004D57",
  "Affiliate Member": "#f1dada",

  // --- TEAL/GREEN SECTION: Inactive ---
  "Charterholder inactive": "#005e6b",
  "Professional member inactive": "#004D57",
  "Other inactive members": "#007a8a",
  "Affiliate Member inactive": "#A9C4BB",
  "Level 3 Eligible": "#1f787f",
  "Level 2 Eligible": "#418b8e",
  "Level 1 Eligible": "#649e9d",
  "L3 Cleared": "#007a8a",
  "Never a Candidate": "#7a9ea8",
  "Never a Member": "#A9C4BB",
};

const ROLE_MAPPING: Record<string, string> = {
  'charterholder': 'Charterholder',
  'level3candidate': 'L3 Candidate',
  'level2candidate': 'L2 Candidate',
  'level1candidate': 'L1 Candidate',
  'professional_member': 'Professional Member',
  'affiliate_member': 'Affiliate Member',
  'charterholderinactive': 'Charterholder (Inact.)',
  'l3eligible': 'L3 Eligible',
  'l2eligible': 'L2 Eligible',
  'l1eligible': 'L1 Eligible',
  'professional_member_inactive': 'Professional (Inact.)',
  'affiliate_member_inactive': 'Affiliate (Inact.)',
  'Never a Candidate': 'Never a Candidate',
  'Never a Member': 'Never a Member',
  'l3_cleared': 'L3 Cleared'
};

const LEGEND_ITEMS = [
  {
    label: "CHARTERHOLDER",
    color: "#680000",
    description: "Certified CMT Charterholder who has completed all three exam levels and meets professional experience requirements."
  },
  {
    label: "PROFESSIONAL MEMBER",
    color: "#8a131a",
    description: "Regular voting member who is actively practicing technical analysis in the financial industry."
  },
  {
    label: "AFFILIATE MEMBER",
    color: "#f1dada",
    description: "Individual interested in technical analysis who supports the CMT Association's mission but does not hold the charter."
  },
  {
    label: "CANDIDATE L3",
    color: "#A9C4BB",
    description: "Registered candidate who has passed Level II and is actively preparing for the final Level III CMT exam."
  },
  {
    label: "CANDIDATE L2",
    color: "#4B8893",
    description: "Registered candidate who has passed Level I and is preparing to clear the Level II CMT exam."
  },
  {
    label: "CANDIDATE L1",
    color: "#004D57",
    description: "Beginning CMT candidate who is preparing to take the foundational Level I CMT exam."
  }
];

interface MatrixRow {
  role: string;
  Total: number;
  [key: string]: string | number;
}

interface SunburstNode {
  name: string;
  itemStyle?: { color: string };
  children?: SunburstNode[];
  value?: number;
}

interface DashboardData {
  error?: string;
  summary: {
    total_volunteers: number;
    active_volunteers: number;
    total_points: number;
    eligible_chapters: number;
  };
  global_sunburst: SunburstNode[];
  regional_sunbursts: Record<string, SunburstNode[]>;
  cluster_sunbursts: Record<string, Record<string, SunburstNode[]>>;
  global_matrix: MatrixRow[];
  regions: string[];
  regional_matrices: Record<string, {
    chapters: string[];
    matrix: MatrixRow[];
  }>;
  chapter_matrices: Record<string, Record<string, {
    clusters: string[];
    matrix: MatrixRow[];
  }>>;
  chapter_eligibility?: Record<string, string>;
  notices?: string[];
}

interface LeaderRow {
  member_id: string;
  first_name: string;
  last_name: string;
  city_name: string;
  city_country: string;
  city_admin1: string;
  cmt_journey_role: string;
  program_journey: string;
  membership_journey: string;
  administration?: string;
  region: string;
  chapter: string;
  cluster_center_name: string;
  nearest_cluster_center_name: string;
  chapter_eligible: string;
  is_active?: boolean;
  has_email?: boolean;
  has_phone?: boolean;
  masked_email?: string;
  masked_phone?: string;
}

export function DirectoryContent({ isEmbedded = false }: { isEmbedded?: boolean }) {
  const router = useRouter();
  const { user, isLoading: authLoading } = useWorkspace();

  // ── Access Control Checks ──────────────────────────────────────────────────
  const hasAccess = useMemo(() => {
    if (!user) return false;

    // Explicitly block users with inactive membership journeys first
    const userMem = (user.membership_journey || "").toLowerCase();
    if (userMem.includes("inactive")) return false;

    const sysRole = (user.role || "").toLowerCase();
    const isStaffOrAdmin = ["admin", "superadmin", "staff"].includes(sysRole) || user.is_staff;
    if (isStaffOrAdmin) return true;

    // Must be active dues-paying member
    const allowedMembership = ["charterholder", "professional_member", "affiliate_member"];
    return allowedMembership.includes(userMem);
  }, [user]);

  const hasStatsAccess = useMemo(() => {
    if (!user) return false;
    const sysRole = (user.role || "").toLowerCase();
    if (["admin", "superadmin", "staff"].includes(sysRole)) return true;
    return !!(user.assignments && user.assignments.length > 0);
  }, [user]);

  const hasMembershipDirectoryAccess = useMemo(() => {
    if (!user) return false;
    const sysRole = (user.role || "").toLowerCase();
    if (["admin", "superadmin", "staff"].includes(sysRole)) return true;

    const allowedDuesPaying = ["charterholder", "professional_member", "affiliate_member"];
    const userMem = (user.membership_journey || "").toLowerCase();
    return allowedDuesPaying.includes(userMem);
  }, [user]);

  const [activeView, setActiveView] = useState<ViewType>("CHAPTER_HUB");
  const [activeSubView, setActiveSubView] = useState<"ANALYTICS" | "DIRECTORY">("ANALYTICS");
  const [directoryType, setDirectoryType] = useState<"leader" | "membership">("leader");

  // Default to Directory view if they don't have stats access
  useEffect(() => {
    if (user && !hasStatsAccess && activeSubView === "ANALYTICS") {
      setActiveSubView("DIRECTORY");
    }
  }, [user, hasStatsAccess, activeSubView]);

  // Default to membership if they don't have leader access
  useEffect(() => {
    if (!hasStatsAccess && directoryType === "leader" && hasMembershipDirectoryAccess) {
      setDirectoryType("membership");
    }
  }, [hasStatsAccess, hasMembershipDirectoryAccess, directoryType]);

  const isMembershipDirectoryBlocked = directoryType === "membership" && !hasMembershipDirectoryAccess;
  const [data, setData] = useState<DashboardData | null>(null);
  const [eligibleData, setEligibleData] = useState<DashboardData | null>(null);
  const [leaderData, setLeaderData] = useState<LeaderRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [hoveredLegend, setHoveredLegend] = useState<string | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);

  const handleErr = (message: string) => {
    setApiError(message);
    console.error(message);
  };

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !user) {
      router.replace("/login?redirectTo=/directory");
    }
  }, [user, authLoading, router]);
  const [loadingEligible, setLoadingEligible] = useState(false);
  const [fetchingLeaders, setFetchingLeaders] = useState(false);
  const leaderFetchAbortRef = useRef<AbortController | null>(null);
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
  const [selectedChapter, setSelectedChapter] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const { replay: replayTour } = useAutoTour("member.directory", TOURS["member.directory"]);
  const [page, setPage] = useState(1);

  const currentData = activeView === "CHAPTER_HUB" ? eligibleData : data;
  const [totalPages, setTotalPages] = useState(1);
  const [totalLeaders, setTotalLeaders] = useState(0);
  const [pageSize] = useState(100);

  // ── Leader Directory Table Logic ──────────────────────────────────────────
  const [sortConfig, setSortConfig] = useState<{ col: string | null, order: 'asc' | 'desc' }>({ col: null, order: 'asc' });

  const leaderViewActive = useMemo(() =>
    activeSubView === "DIRECTORY",
    [activeSubView]
  );
  const [filterOptions, setFilterOptions] = useState<{
    regions: string[];
    chapters: string[];
    roles: string[];
    clusters: string[];
    countries: string[];
    localities: string[];
    nearest_clusters: string[];
    chapter_eligibility: string[];
    states: string[];
    program_journeys: string[];
    membership_journeys: string[];
  }>({ regions: [], chapters: [], roles: [], clusters: [], countries: [], localities: [], nearest_clusters: [], chapter_eligibility: [], states: [], program_journeys: [], membership_journeys: [] });
  const [filterRegion, setFilterRegion] = useState<string[]>([]);
  const [excludeRegion, setExcludeRegion] = useState(false);
  const [filterChapter, setFilterChapter] = useState<string[]>([]);
  const [excludeChapter, setExcludeChapter] = useState(false);
  const [filterRole, setFilterRole] = useState<string[]>([]);
  const [excludeRole, setExcludeRole] = useState(false);
  const [filterCluster, setFilterCluster] = useState<string[]>([]);
  const [excludeCluster, setExcludeCluster] = useState(false);
  const [filterCountry, setFilterCountry] = useState<string[]>([]);
  const [excludeCountry, setExcludeCountry] = useState(false);
  const [filterLocality, setFilterLocality] = useState<string[]>([]);
  const [excludeLocality, setExcludeLocality] = useState(false);
  const [filterState, setFilterState] = useState<string[]>([]);
  const [excludeState, setExcludeState] = useState(false);
  const [filterNearestCluster, setFilterNearestCluster] = useState<string[]>([]);
  const [excludeNearestCluster, setExcludeNearestCluster] = useState(false);
  const [filterChapterEligible, setFilterChapterEligible] = useState<string[]>([]);
  const [excludeChapterEligible, setExcludeChapterEligible] = useState(false);
  const [filterNameSearch, setFilterNameSearch] = useState("");
  const [debouncedNameSearch, setDebouncedNameSearch] = useState("");
  const [filterIdSearch, setFilterIdSearch] = useState("");
  const [debouncedIdSearch, setDebouncedIdSearch] = useState("");
  const [filterProgramJourney, setFilterProgramJourney] = useState<string[]>([]);
  const [excludeProgramJourney, setExcludeProgramJourney] = useState(false);
  const [filterMembershipJourney, setFilterMembershipJourney] = useState<string[]>([]);
  const [excludeMembershipJourney, setExcludeMembershipJourney] = useState(false);
  const [filterActive, setFilterActive] = useState<boolean | null>(null);

  // ── Contact Reveal State ────────────────────────────────────────────────
  const [activeRevealId, setActiveRevealId] = useState<string | null>(null);
  const [activeContactData, setActiveContactData] = useState<{ emails: string[], phones: string[] } | null>(null);
  const [isRevealing, setIsRevealing] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const handleReveal = async (member_id: string) => {
    // Toggle off if clicking the same person
    if (activeRevealId === member_id) {
      setActiveRevealId(null);
      setActiveContactData(null);
      return;
    }

    setIsRevealing(true);
    // Setting the ID immediately shows the "loading" states in BOTH email and phone columns
    setActiveRevealId(member_id);
    setActiveContactData(null);

    try {
      const res = await fetchWithAuth(`${API_BASE}/api/v1/directory/leader-contact/${member_id}`);
      if (!res.ok) {
        throw new Error(`Failed to reveal contact info: ${res.status} ${res.statusText}`);
      }
      const data = await res.json();
      // Ensure we haven't switched to another user while fetching
      setActiveContactData(data);
    } catch (err: any) {
      handleErr(err.message || "Error revealing contact");
      setActiveRevealId(null);
    } finally {
      setIsRevealing(false);
    }
  };

  const handleCopy = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const chartRef = useRef<ReactECharts>(null);

  // Debounce search term — reset to page 1 on new search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedNameSearch(filterNameSearch);
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [filterNameSearch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedIdSearch(filterIdSearch);
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [filterIdSearch]);

  // Load global stats once on mount
  useEffect(() => {
    if (!user || !hasAccess || !hasStatsAccess || data) {
      if (user && (!hasAccess || !hasStatsAccess)) {
        setLoading(false);
      }
      return;
    }
    fetchWithAuth(`${API_BASE}/api/v1/directory/stats`)
      .then(res => {
        if (!res.ok) {
          throw new Error(`Failed to fetch stats: ${res.status} ${res.statusText}`);
        }
        return res.json();
      })
      .then(d => {
        setData(d);
        setLoading(false);
      })
      .catch(err => {
        handleErr(err.message || "Error fetching stats");
        setLoading(false);
      });
  }, [user, data, hasAccess, hasStatsAccess]);

  // Load eligible chapter stats when CHAPTERS tab is first opened
  useEffect(() => {
    if (!hasAccess || !hasStatsAccess) return;
    if (activeView === "CHAPTER_HUB" && !eligibleData && !loadingEligible && user) {
      setLoadingEligible(true);
      fetchWithAuth(`${API_BASE}/api/v1/directory/stats?eligible_only=true`)
        .then(res => {
          if (!res.ok) {
            throw new Error(`Failed to fetch eligible stats: ${res.status} ${res.statusText}`);
          }
          return res.json();
        })
        .then(d => {
          setEligibleData(d);
          setLoadingEligible(false);
        })
        .catch(err => {
          handleErr(err.message || "Error fetching eligible stats");
          setLoadingEligible(false);
        });
    }
  }, [activeView, user, eligibleData, loadingEligible, hasAccess, hasStatsAccess]);

  // Unified leader data + filter fetch — fires once per dependency change, with AbortController to cancel stale requests
  useEffect(() => {
    if (!hasAccess || !leaderViewActive) return;

    // Cancel any in-flight request
    if (leaderFetchAbortRef.current) {
      leaderFetchAbortRef.current.abort();
    }
    const controller = new AbortController();
    leaderFetchAbortRef.current = controller;

    setFetchingLeaders(true);

    const buildParams = (extra?: Record<string, string>) => {
      const params = new URLSearchParams(extra);
      if (debouncedSearch) params.append("search", debouncedSearch);
      filterRegion.forEach(r => params.append("region", r));
      if (excludeRegion && filterRegion.length > 0) params.append("region_exclude", "true");
      filterChapter.forEach(c => params.append("chapter", c));
      if (excludeChapter && filterChapter.length > 0) params.append("chapter_exclude", "true");
      filterRole.forEach(r => params.append("role", r));
      if (excludeRole && filterRole.length > 0) params.append("role_exclude", "true");
      filterCluster.forEach(c => params.append("cluster", c));
      if (excludeCluster && filterCluster.length > 0) params.append("cluster_exclude", "true");
      filterCountry.forEach(c => params.append("country", c));
      if (excludeCountry && filterCountry.length > 0) params.append("country_exclude", "true");
      filterLocality.forEach(l => params.append("locality", l));
      if (excludeLocality && filterLocality.length > 0) params.append("locality_exclude", "true");
      filterNearestCluster.forEach(n => params.append("nearest_cluster", n));
      if (excludeNearestCluster && filterNearestCluster.length > 0) params.append("nearest_cluster_exclude", "true");
      filterChapterEligible.forEach(c => params.append("chapter_eligible", c));
      if (excludeChapterEligible && filterChapterEligible.length > 0) params.append("chapter_eligible_exclude", "true");
      filterState.forEach(s => params.append("state", s));
      if (excludeState && filterState.length > 0) params.append("state_exclude", "true");
      if (debouncedNameSearch) params.append("name_search", debouncedNameSearch);
      if (debouncedIdSearch) params.append("id_search", debouncedIdSearch);
      filterProgramJourney.forEach(p => params.append("program_journey", p));
      if (excludeProgramJourney && filterProgramJourney.length > 0) params.append("program_journey_exclude", "true");
      filterMembershipJourney.forEach(m => params.append("membership_journey", m));
      if (excludeMembershipJourney && filterMembershipJourney.length > 0) params.append("membership_journey_exclude", "true");
      if (filterActive !== null) params.append("is_active", filterActive.toString());
      if (directoryType) params.append("directory_type", directoryType);
      return params;
    };

    const dirParams = buildParams({
      page: page.toString(),
      page_size: pageSize.toString(),
    });
    if (sortConfig.col) {
      dirParams.append("sort_col", sortConfig.col);
      dirParams.append("sort_order", sortConfig.order);
    }

    const filterParams = buildParams();

    Promise.all([
      fetchWithAuth(`${API_BASE}/api/v1/directory/leader-directory?${dirParams.toString()}`, { signal: controller.signal }),
      fetchWithAuth(`${API_BASE}/api/v1/directory/leader-filters?${filterParams.toString()}`, { signal: controller.signal }),
    ])
      .then(([dirRes, filterRes]) => {
        if (!dirRes.ok) {
          throw new Error(`Failed to fetch leader directory: ${dirRes.status} ${dirRes.statusText}`);
        }
        if (!filterRes.ok) {
          throw new Error(`Failed to fetch filters: ${filterRes.status} ${filterRes.statusText}`);
        }
        return Promise.all([dirRes.json(), filterRes.json()]);
      })
      .then(([dirData, filterData]) => {
        setLeaderData(dirData.data || []);
        setTotalPages(dirData.total_pages || 1);
        setTotalLeaders(dirData.total || 0);
        setFilterOptions(filterData);
        setFetchingLeaders(false);
      })
      .catch(err => {
        if (err.name !== "AbortError") {
          handleErr(err.message || "Error fetching leader data");
          setFetchingLeaders(false);
        }
      });

    return () => { controller.abort(); };
  }, [hasAccess, leaderViewActive, page, debouncedSearch, debouncedNameSearch, debouncedIdSearch, pageSize, filterRegion, filterChapter, filterRole, filterCluster, filterCountry, filterLocality, filterNearestCluster, filterChapterEligible, filterState, filterProgramJourney, filterMembershipJourney, filterActive, excludeRegion, excludeChapter, excludeRole, excludeCluster, excludeCountry, excludeLocality, excludeNearestCluster, excludeChapterEligible, excludeState, excludeProgramJourney, excludeMembershipJourney, sortConfig, directoryType]); // eslint-disable-line react-hooks/exhaustive-deps

  const onChartClick = (params: { componentType?: string; name: string; data?: { name: string }; treePathInfo?: { name: string }[] }) => {
    if (!hasAccess) return;

    // ── Back button (graphic element) ──
    if (params.componentType === "graphic") {
      handleBack();
      return;
    }
    const currentData = activeView === "CHAPTER_HUB" ? eligibleData : data;
    if (!currentData) return;

    // In Global View: Inner ring is Region
    if (!selectedRegion) {
      if (params.data && params.data.name && currentData.regions.includes(params.data.name)) {
        setSelectedRegion(params.data.name);
      }
    } else if (!selectedChapter) {
      // In Regional View: Inner ring is Chapter
      const path = params.treePathInfo || [];
      const chapterNode = path[1]; // Level 1 (Inner ring) in the regional subset
      const regData = currentData.regional_matrices[selectedRegion];

      if (chapterNode && regData?.chapters.includes(chapterNode.name)) {
        setSelectedChapter(chapterNode.name);
      }
    }
  };

  const handleBack = () => {
    if (selectedChapter) {
      setSelectedChapter(null);
    } else if (selectedRegion) {
      setSelectedRegion(null);
    }
  };

  // ── 1. Palette inspired by the reference screenshot ─────────────────────────
  //    Inner ring: dark, rich tones per region
  //    Outer ring: each region's muted tint, with teal/crimson accents

  // Brand Colors from styleguide.md
  const BRAND_ACCENT = "#EF4621";

  // ── 2. Graphic element: center "BACK" circle (only when drilled in) ──────────
  //    We inject this via the ECharts `graphic` option.
  const makeBackGraphic = (show: boolean) =>
    (show && hasAccess)
      ? [
        {
          type: "circle",
          left: "center",
          top: "center",
          shape: { r: 44 },
          style: {
            fill: BRAND_ACCENT,
            shadowBlur: 20,
            shadowColor: "rgba(239,70,33,0.45)",
          },
          z: 100,
          cursor: "pointer",
        },
        {
          type: "text",
          left: "center",
          top: "center",
          style: {
            text: "BACK",
            fill: "#ffffff",
            font: "400 13px 'Glacial Indifference', sans-serif",
            textAlign: "center",
            textVerticalAlign: "middle",
          },
          z: 101,
          cursor: "pointer",
        },
      ]
      : [
        // Empty center when not drilled — subtle ring only
        {
          type: "circle",
          left: "center",
          top: "center",
          shape: { r: 44 },
          style: { fill: "rgba(0,77,87,0.04)", stroke: "rgba(0,77,87,0.08)", lineWidth: 1.5 },
          z: 100,
        },
      ];

  const colorizeChartData = (nodes: SunburstNode[]): SunburstNode[] =>
    nodes.map((node: SunburstNode, i: number) => ({
      ...node,
      itemStyle: {
        ...(node.itemStyle || {}),
        color: INNER_PALETTE[i % INNER_PALETTE.length],
      },
      children: (node.children || []).map((child: SunburstNode) => ({
        ...child,
        itemStyle: {
          ...(child.itemStyle || {}),
          color:
            ROLE_COLOR_MAP[child.name] ??
            ROLE_PALETTE[
            Math.abs(
              (child.name || "").split("").reduce((a: number, c: string) => a + c.charCodeAt(0), 0)
            ) % ROLE_PALETTE.length
            ],
        },
      })),
    }));

  // ── 3. Main sunburst option ──────────────────────────────────────────────────
  //    Call this as a function so it re-evaluates when selectedRegion changes.
  const getSunburstOption = (
    chartData: SunburstNode[],
    isDrilled: boolean       // true when a region or chapter is selected
  ) => ({
    backgroundColor: "transparent",   // card bg handles background

    graphic: makeBackGraphic(isDrilled),

    tooltip: {
      trigger: "item",
      backgroundColor: "rgba(15,17,28,0.90)",
      borderColor: "transparent",
      borderRadius: 10,
      padding: [10, 16],
      textStyle: {
        color: "#f1f5f9",
        fontFamily: "'Glacial Indifference', sans-serif",
        fontSize: 12,
        fontWeight: 500,
      },
      formatter: (params: { name: string; value?: number; data?: { uniqueCount?: number } }) => {
        const displayValue = hasStatsAccess ? (params.data?.uniqueCount ?? params.value) : null;
        const currentData = activeView === "CHAPTER_HUB" ? eligibleData : data;
        const eligibility = currentData?.chapter_eligibility?.[params.name];
        const eligibilityHtml =
          (activeView === "CHAPTER_HUB" && eligibility === "partial")
            ? '<br/><span style="color:#fbbf24;font-size:10px;font-weight:700">● PARTIAL ELIGIBILITY</span>'
            : (activeView === "CHAPTER_HUB" && eligibility === "proposed")
              ? '<br/><span style="color:#3b82f6;font-size:10px;font-weight:700">● PROPOSED CHAPTER</span>'
              : (activeView === "CHAPTER_HUB" && eligibility === "recommended")
                ? '<br/><span style="color:#10b981;font-size:10px;font-weight:700">● RECOMMENDED COMMUNITY</span>'
                : "";
        return `<span style="font-weight:700">${params.name}</span>${displayValue ? `&nbsp;&nbsp;<span style="color:#d1d5db">${Number(displayValue).toLocaleString()}</span>` : ""
          }${eligibilityHtml}`;
      },
    },

    series: {
      type: "sunburst",
      data: colorizeChartData(chartData),

      // Fill container better: push radius from 85% → 95%
      radius: ["4%", "95%"],
      sort: undefined,

      nodeClick: false,   // we handle clicks manually via onEvents

      emphasis: {
        focus: "ancestor",
        itemStyle: {
          shadowBlur: 24,
          shadowColor: "rgba(0,0,0,0.35)",
        },
      },

      levels: [
        // Level 0 — invisible root
        {},

        // ── Level 1: Regions / Chapters (INNER, bigger ring) ─────────────────
        {
          r0: "8%",
          r: "48%",         // ← increased to 48% to highlight regions/chapters
          label: {
            rotate: "radial",
            color: "#ffffff",
            fontFamily: "'Glacial Indifference', sans-serif",
            fontWeight: 400,
            fontSize: 11,
            overflow: "truncate",
            minAngle: 5,
            width: 120,
          },
          itemStyle: {
            borderWidth: 3,
            borderColor: "rgba(255,255,255,0.12)",
            borderRadius: 0,
          },
        },

        // ── Level 2: Roles (OUTER ring) ───────────────────────────────────────
        {
          r0: "55%",        // matches inner ring's end
          r: "65%",         // ← further reduced to 60% to avoid clipping
          label: {
            position: "outside",
            padding: 6,
            silent: false,
            fontFamily: "'Glacial Indifference', sans-serif",
            fontSize: 9,
            fontWeight: 400,
            color: "#94a3b8",          // changed from #1A1A1A to resolve user's "black colour" issue
            minAngle: 7,               // hide label if segment < 7° → no overlap
            overflow: "truncate",
            width: 110,
          },
          itemStyle: {
            borderWidth: 1.5,
            borderColor: "rgba(255,255,255,0.25)",
            borderRadius: 3,
            opacity: 0.88,
          },
        },
      ],
    },
  });

  // Logic for Dynamic Table
  const getTableConfig = () => {
    if (!currentData) return null;

    let title = activeView === "CHAPTER_HUB" ? "Eligible CHAPTERS STATISTICS" : "GLOBAL STATISTICS";
    let columns: string[] = currentData.regions || [];
    let rows: any[] = currentData.global_matrix || [];

    if (selectedRegion && currentData.regional_matrices?.[selectedRegion]) {
      const regData = currentData.regional_matrices[selectedRegion];
      if (selectedChapter) {
        const chapData = currentData.chapter_matrices?.[selectedRegion]?.[selectedChapter];
        if (chapData) {
          title = `STATISTICS: ${selectedChapter} (${selectedRegion})`;
          columns = chapData.clusters || [];
          rows = chapData.matrix || [];
        } else {
          // Fallback if chapData is missing
          title = `STATISTICS: ${selectedChapter} (${selectedRegion})`;
          columns = [selectedChapter];
          rows = (regData.matrix || []).map(row => ({
            role: row.role,
            Total: row[selectedChapter] as number,
            [selectedChapter]: row[selectedChapter]
          }));
        }
      } else {
        title = `REGIONAL STATISTICS: ${selectedRegion}`;
        columns = regData.chapters || [];
        rows = regData.matrix || [];
      }
    }

    // Filter rows by search term
    const normalizeString = (str: string) =>
      str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

    if (searchTerm) {
      const normSearch = normalizeString(searchTerm);
      rows = rows.filter(row => row.role && normalizeString(row.role).includes(normSearch));
    }

    return { title, columns, rows };
  };

  const tableConfig = getTableConfig();

  // ── Leader Directory Table Logic ──────────────────────────────────────────

  if (!authLoading && user && !hasAccess) {
    return (
      <div className="min-h-screen bg-[#F0F0F0] flex items-center justify-center p-6" style={{ fontFamily: "'Glacial Indifference', sans-serif" }}>
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="max-w-2xl w-full bg-white rounded-[3rem] shadow-2xl border border-slate-100 overflow-hidden relative"
        >
          {/* Top Decorative bar */}
          <div className="h-4 bg-[#AE232D]" />

          <div className="p-10 lg:p-14 text-center space-y-8">
            <div className="w-20 h-20 bg-[#004D57]/5 rounded-[2rem] flex items-center justify-center mx-auto border border-[#004D57]/10 text-[#004D57]">
              <Users className="w-10 h-10 text-[#AE232D]" />
            </div>

            <div className="space-y-4">
              <h2 className="text-3xl font-normal uppercase tracking-tight text-[#004D57] leading-tight">
                Restricted Intelligence Access
              </h2>
              <div className="w-16 h-1 bg-[#AE232D] mx-auto rounded-full" />
              <p className="text-slate-500 font-medium leading-relaxed max-w-md mx-auto uppercase tracking-wide text-xs">
                This dashboard is exclusively reserved for members actively engaged in the CMT Experience.
              </p>
            </div>

            <div className="bg-[#004D57]/5 border border-[#004D57]/10 rounded-2xl p-6 text-left space-y-3">
              <span className="text-[10px] font-bold text-[#004D57] uppercase tracking-widest block border-b border-[#004D57]/10 pb-2">Allowed Roles Overview:</span>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[10px] text-slate-600 font-bold uppercase tracking-wider">
                <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-[#AE232D] rounded-full" /> CMT Charterholders</li>
                <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-[#AE232D] rounded-full" /> Professional Members</li>
                <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-[#AE232D] rounded-full" /> Affiliate Members</li>
                <li className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-[#AE232D] rounded-full" /> Group Volunteers</li>
              </ul>
            </div>

            <p className="text-slate-400 font-normal uppercase tracking-widest text-[9px] leading-relaxed">
              If you believe you should have access, please verify your profile credentials or contact CMT Association support.
            </p>

            <div className="pt-2 flex flex-wrap gap-4 justify-center">
              <Button
                onClick={() => window.open("https://cmtassociation.org/membership", "_blank")}
                className="bg-[#004D57] hover:bg-[#003c44] text-white px-6 py-3 rounded-xl font-bold uppercase tracking-widest text-[10px] transition-all shadow-md"
              >
                Renew Membership
              </Button>
              <Button
                onClick={() => router.push("/dashboard?section=gigs")}
                className="bg-[#AE232D] hover:bg-[#8b1c24] text-white px-6 py-3 rounded-xl font-bold uppercase tracking-widest text-[10px] transition-all shadow-md"
              >
                Apply for Gigs
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className={cn("bg-[#F0F0F0] text-[#1A1A1A]", isEmbedded ? "min-h-[calc(100vh-200px)] rounded-[2rem]" : "min-h-screen pb-20 overflow-x-hidden")} style={{ fontFamily: "'Glacial Indifference', sans-serif" }}>
      <AnimatePresence>
        {loading && (
          <motion.div
            initial={{ opacity: 1 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-0 z-[100] bg-[#004D57] flex flex-col items-center justify-center text-white p-10 text-center space-y-8"
          >
            <motion.div
              animate={{
                scale: [1, 1.1, 1],
                rotate: [0, 5, -5, 0]
              }}
              transition={{ repeat: Infinity, duration: 3 }}
              className="w-24 h-24 bg-[#AE232D] rounded-2xl shadow-2xl flex items-center justify-center mb-4"
            >
              <LayoutDashboard className="w-12 h-12 text-white" />
            </motion.div>

            <div className="space-y-4 max-w-md">
              <h2 className="text-3xl font-normal uppercase tracking-[0.3em]">Initializing Dashboard</h2>
              <div className="w-20 h-1 bg-[#AE232D] mx-auto"></div>
              <p className="text-sm font-normal text-[#A9C4BB] uppercase tracking-[0.15em] leading-relaxed">
                Loading Global Intelligence Network...<br />
                Analyzing volunteer footprints and chapter density.
              </p>
            </div>

            <div className="flex items-center gap-2 px-6 py-3 bg-white/5 rounded-full border border-white/10">
              <div className="w-2 h-2 bg-[#AE232D] rounded-full animate-pulse"></div>
              <span className="text-[10px] font-normal uppercase tracking-widest opacity-60">System Synchronizing</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="w-full max-w-[1700px] mx-auto px-6 pt-0 lg:pt-0 space-y-4">
        {/* ── Premium Top Control Header ───────────────────────────────────── */}
        <header className="bg-white/80 backdrop-blur-md rounded-2xl p-4 border border-slate-100 shadow-xl flex flex-col lg:flex-row items-center justify-between gap-4">
          <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
            {[
              { id: "DIRECTORY", label: "Directory", desc: "Detailed chapter leadership list.", icon: Users, hidden: false },
              { id: "ANALYTICS", label: "Analytics", desc: "Interactive maps, charts, and metrics.", icon: LayoutDashboard, hidden: !hasStatsAccess }
            ].filter((tab) => !tab.hidden).map((tab) => (
              <motion.button
                key={tab.id}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  setActiveSubView(tab.id as "ANALYTICS" | "DIRECTORY");
                }}
                className={cn(
                  "p-3.5 px-5 rounded-xl cursor-pointer transition-all border-2 text-left flex-1 min-w-[250px] relative overflow-hidden",
                  activeSubView === tab.id
                    ? "bg-[#004D57] border-[#AE232D] shadow-lg text-white"
                    : "bg-[#F8F9FA] border-transparent hover:border-[#004D57]/20 text-[#004D57]"
                )}
              >
                <div className="flex items-center justify-between w-full">
                  <div>
                    <p className="text-[13px] font-bold uppercase tracking-tight leading-none mb-1">{tab.label}</p>
                    <p className="text-[10px] font-medium opacity-60 uppercase tracking-tight leading-tight">{tab.desc}</p>
                  </div>
                  <tab.icon className={cn("w-6 h-6 shrink-0 ml-4", activeSubView === tab.id ? "text-[#AE232D]" : "text-[#004D57]/30")} />
                </div>
              </motion.button>
            ))}
          </div>

          {/* Sub-navigation: Footprint / Scope selection */}
          <div className={cn(
            "flex gap-2 bg-slate-100/80 p-1 rounded-xl border border-slate-200 w-full lg:w-auto transition-all duration-300",
            activeSubView === "DIRECTORY" && "blur-[1.5px] opacity-40 grayscale pointer-events-none select-none"
          )}>
            {[
              { id: "FOOTPRINT", label: "Global Footprint Overview", icon: Globe },
              { id: "CHAPTER_HUB", label: "Eligible Chapters Hub", icon: Shield }
            ].map((view) => (
              <button
                key={view.id}
                disabled={activeSubView === "DIRECTORY"}
                onClick={() => {
                  setActiveView(view.id as ViewType);
                  if (view.id === "FOOTPRINT" || view.id === "CHAPTER_HUB") {
                    setSelectedRegion(null);
                    setSelectedChapter(null);
                  }
                }}
                className={cn(
                  "flex items-center gap-2.5 px-5 py-3 rounded-lg border-2 transition-all duration-300 font-bold uppercase tracking-widest text-[10px] flex-1 lg:flex-initial justify-center",
                  activeView === view.id && activeSubView === "ANALYTICS"
                    ? "bg-[#004D57] border-[#AE232D] text-white shadow-md"
                    : "border-transparent text-[#004D57] hover:bg-white/50"
                )}
              >
                <view.icon className={cn("w-4 h-4", activeView === view.id && activeSubView === "ANALYTICS" ? "text-[#AE232D]" : "text-[#004D57]/40")} />
                {view.label}
              </button>
            ))}
          </div>
        </header>

        {apiError && (
          <div className="bg-[#AE232D]/10 border border-[#AE232D] text-[#AE232D] p-4 rounded-xl flex items-center justify-between shadow-md">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              <span className="text-sm font-semibold tracking-wide uppercase">{apiError}</span>
            </div>
            <button onClick={() => setApiError(null)} className="text-sm font-bold uppercase tracking-widest hover:underline cursor-pointer">
              Dismiss
            </button>
          </div>
        )}


        <div className="flex flex-col xl:grid xl:grid-cols-12 gap-6 items-stretch">
          {/* ── Center Content: Chart or Directory (9/12 or 12/12) ────────── */}
          <main className={cn(
            "flex flex-col gap-4 min-w-0 transition-all duration-300",
            (activeSubView === "DIRECTORY") ? "xl:col-span-12" : "xl:col-span-9"
          )}>

            {activeSubView === "DIRECTORY" ? (
              /* LEADER DIRECTORY VIEW */
              <motion.section
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                className="space-y-4"
              >
                <div className="px-2 relative z-50">
                  <div className="inline-flex bg-white/60 backdrop-blur-md p-1 rounded-xl border border-slate-200/50 shadow-sm gap-1.5">
                    {[
                      { id: "leader", label: "Volunteer Directory", hidden: !hasStatsAccess },
                      { id: "membership", label: "Membership Directory", hidden: !hasMembershipDirectoryAccess }
                    ].filter(d => !d.hidden).map((dir) => (
                      <div key={dir.id} className="relative group">
                        <button
                          onClick={() => {
                            setDirectoryType(dir.id as "leader" | "membership");
                            setPage(1);
                          }}
                          className={cn(
                            "px-5 py-2.5 rounded-lg text-xs font-bold uppercase tracking-widest transition-all duration-300 flex items-center gap-2",
                            directoryType === dir.id
                              ? "bg-[#004D57] text-white shadow-md"
                              : "text-[#004D57]/70 hover:bg-slate-50 hover:text-[#004D57]"
                          )}
                        >
                          {dir.label}
                        </button>
                        <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-64 p-3.5 bg-[#004D57] text-white text-[9px] font-normal uppercase tracking-widest rounded-xl shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-[100] border border-white/10 text-center leading-relaxed">
                          Note: This directory only displays members with complete geographic/address profiles. Users without registered address details are excluded.
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex flex-col gap-4 px-2">
                  {directoryType === "membership" ? (
                    <div className="space-y-1">
                      <h2 className="text-3xl font-normal text-[#004D57] uppercase tracking-tighter flex items-center gap-4">
                        <Users className="w-8 h-8 text-[#AE232D]" />
                        Membership Directory
                      </h2>
                      <p className="text-slate-500 text-[10px] font-normal uppercase tracking-widest">
                        Restricted Access: Contains details of registered Charterholders, Professional Members, and Affiliate Members.
                      </p>
                    </div>
                  ) : null}

                  <div className="flex flex-col sm:flex-row sm:flex-wrap items-center gap-3 w-full justify-start">
                    <div id="directory-search" className="relative group flex-1">
                      <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-slate-400 group-focus-within:text-[#004D57] transition-colors" />
                      <Input
                        placeholder="GLOBAL KEYWORD SEARCH..."
                        className="pl-11 h-11 bg-white border-slate-100 rounded-xl shadow-md focus:ring-2 focus:ring-[#004D57] font-normal text-xs tracking-widest uppercase"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>

                    <div className="flex bg-slate-100 p-1 rounded-xl border border-slate-200">
                      {[
                        { label: 'ALL', value: null },
                        { label: 'ACTIVE', value: true },
                        { label: 'INACTIVE', value: false }
                      ].map((opt) => (
                        <button
                          key={opt.label}
                          onClick={() => { setFilterActive(opt.value); setPage(1); }}
                          className={cn(
                            "px-4 py-2 rounded-lg text-[9px] font-bold tracking-widest transition-all",
                            filterActive === opt.value
                              ? "bg-white text-[#004D57] shadow-sm"
                              : "text-slate-400 hover:text-slate-600"
                          )}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                    <TourButton onClick={replayTour} />
                    {(filterRegion.length > 0 || filterChapter.length > 0 || filterRole.length > 0 || filterCluster.length > 0 || filterCountry.length > 0 || filterLocality.length > 0 || filterNearestCluster.length > 0 || filterChapterEligible.length > 0 || filterState.length > 0 || searchTerm || filterNameSearch || filterIdSearch || filterActive !== null || filterProgramJourney.length > 0 || filterMembershipJourney.length > 0) && (
                      <Button
                        onClick={() => {
                          setFilterRegion([]);
                          setExcludeRegion(false);
                          setFilterChapter([]);
                          setExcludeChapter(false);
                          setFilterRole([]);
                          setExcludeRole(false);
                          setFilterCluster([]);
                          setExcludeCluster(false);
                          setFilterCountry([]);
                          setExcludeCountry(false);
                          setFilterLocality([]);
                          setExcludeLocality(false);
                          setFilterNearestCluster([]);
                          setExcludeNearestCluster(false);
                          setFilterChapterEligible([]);
                          setExcludeChapterEligible(false);
                          setFilterState([]);
                          setExcludeState(false);
                          setFilterNameSearch("");
                          setFilterIdSearch("");
                          setFilterProgramJourney([]);
                          setExcludeProgramJourney(false);
                          setFilterMembershipJourney([]);
                          setExcludeMembershipJourney(false);
                          setFilterActive(null);
                          setSearchTerm("");
                          setPage(1);
                        }}
                        variant="ghost"
                        className="h-10 px-4 border border-dashed border-[#AE232D]/30 text-[#AE232D] font-bold text-[9px] uppercase tracking-widest hover:bg-[#AE232D]/5 rounded-xl transition-all"
                      >
                        Clear Filters
                      </Button>
                    )}
                  </div>
                </div>

                <div className="bg-white rounded-2xl border border-slate-50 shadow-xl overflow-hidden p-1 relative min-h-[600px] flex flex-col">
                  {/* Thin progress bar — visible during active fetch, replaces full-table spinner */}
                  {fetchingLeaders && (
                    <div className="absolute top-2 left-2 right-2 h-0.5 z-30 overflow-hidden rounded-full">
                      <div className="h-full bg-[#AE232D] rounded-full" style={{ animation: 'progress-slide 1.2s ease-in-out infinite' }} />
                    </div>
                  )}
                  <div className={cn("flex-1 flex flex-col relative", isMembershipDirectoryBlocked && "blur-[6px] opacity-40 pointer-events-none select-none")}>
                    {/* Top Pagination Controls */}
                    <div className="px-6 py-4 bg-slate-50 border-b border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-4 rounded-t-xl">
                      <span className="text-[10px] font-normal text-[#004D57] uppercase tracking-widest">
                        {totalLeaders.toLocaleString()} Total Records &nbsp;·&nbsp; Showing page {page} of {totalPages}
                      </span>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => setPage(p => Math.max(1, p - 1))}
                          disabled={page <= 1 || fetchingLeaders}
                          className="flex items-center gap-1.5 px-5 py-2.5 bg-white border border-slate-200 rounded-xl font-normal text-[9px] uppercase tracking-widest text-[#004D57] hover:bg-[#004D57] hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <ChevronLeft className="w-3 h-3" /> Prev
                        </button>
                        <span className="text-[10px] font-normal text-slate-400 tabular-nums">
                          {page} / {totalPages}
                        </span>
                        <button
                          onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                          disabled={page >= totalPages || fetchingLeaders}
                          className="flex items-center gap-1.5 px-5 py-2.5 bg-[#004D57] text-white rounded-xl font-normal text-[9px] uppercase tracking-widest hover:bg-[#005D69] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          Next <ChevronRight className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                    <DualScrollWrapper>
                      <div id="directory-table" className="rounded-2xl border border-slate-50 relative min-w-max">
                        <table className="w-full border-collapse text-left">
                          <thead className="sticky top-0 z-20 shadow-sm">
                            <tr className="bg-[#004D57] text-white">
                              <th className="p-1 px-2 font-normal uppercase text-[9px] tracking-[0.2em] border-r border-white/10 whitespace-nowrap min-w-[120px]">
                                <div className="flex flex-col gap-2">
                                  <SortToggle col="name" sortConfig={sortConfig} setSortConfig={setSortConfig} />
                                  <div className="relative group/search">
                                    <Input
                                      type="text"
                                      placeholder="NAME..."
                                      value={filterNameSearch}
                                      onChange={(e) => setFilterNameSearch(e.target.value)}
                                      className="h-6 bg-white/5 border-white/10 text-[9px] text-white placeholder:text-white/30 focus:bg-white/10 focus:border-white/20 transition-all pl-5 pr-2 rounded-md font-normal lowercase tracking-tight w-full max-w-[110px]"
                                    />
                                    <Search className="absolute left-1.5 top-1/2 -translate-y-1/2 w-2.5 h-2.5 text-white/40 group-focus-within/search:text-white/70 transition-colors" />
                                  </div>
                                </div>
                              </th>
                              <th className="p-1 px-2 font-normal uppercase text-[9px] tracking-[0.2em] border-r border-white/10 whitespace-nowrap min-w-[70px]">
                                <div className="flex flex-col gap-2">
                                  <SortToggle col="program_journey" sortConfig={sortConfig} setSortConfig={setSortConfig} />
                                  <PremiumMultiSelect
                                    label="PROGRAM JOURNEY"
                                    plural="PROGRAM STATUSES"
                                    options={filterOptions.program_journeys}
                                    selected={filterProgramJourney}
                                    isExclude={excludeProgramJourney}
                                    onExcludeToggle={setExcludeProgramJourney}
                                    onChange={(vals) => { setFilterProgramJourney(vals); setPage(1); }}
                                  />
                                </div>
                              </th>
                              <th className="p-1 px-2 font-normal uppercase text-[9px] tracking-[0.2em] border-r border-white/10 whitespace-nowrap min-w-[70px]">
                                <div className="flex flex-col gap-2">
                                  <SortToggle col="membership_journey" sortConfig={sortConfig} setSortConfig={setSortConfig} />
                                  <PremiumMultiSelect
                                    label="MEMBERSHIP JOURNEY"
                                    plural="MEMBERSHIP STATUSES"
                                    options={filterOptions.membership_journeys}
                                    selected={filterMembershipJourney}
                                    isExclude={excludeMembershipJourney}
                                    onExcludeToggle={setExcludeMembershipJourney}
                                    onChange={(vals) => { setFilterMembershipJourney(vals); setPage(1); }}
                                  />
                                </div>
                              </th>
                              <th className="p-1 px-2 font-normal uppercase text-[9px] tracking-[0.2em] border-r border-white/10 whitespace-nowrap min-w-[70px]">
                                <div className="flex flex-col gap-2">
                                  <SortToggle col="cluster" sortConfig={sortConfig} setSortConfig={setSortConfig} />
                                  <PremiumMultiSelect
                                    label="COMMUNITY"
                                    plural="COMMUNITIES"
                                    options={filterOptions.clusters}
                                    selected={filterCluster}
                                    isExclude={excludeCluster}
                                    onExcludeToggle={setExcludeCluster}
                                    onChange={(vals) => { setFilterCluster(vals); setPage(1); }}
                                  />
                                </div>
                              </th>
                              <th className="p-1 px-2 font-normal uppercase text-[9px] tracking-[0.2em] border-r border-white/10 whitespace-nowrap min-w-[70px]">
                                <div className="flex flex-col gap-2">
                                  <SortToggle col="nearest_cluster" sortConfig={sortConfig} setSortConfig={setSortConfig} />
                                  <PremiumMultiSelect
                                    label="NEAREST COMMUNITY"
                                    plural="NEAREST COMMUNITIES"
                                    options={filterOptions.nearest_clusters}
                                    selected={filterNearestCluster}
                                    isExclude={excludeNearestCluster}
                                    onExcludeToggle={setExcludeNearestCluster}
                                    onChange={(vals) => { setFilterNearestCluster(vals); setPage(1); }}
                                  />
                                </div>
                              </th>
                              <th className="p-1 px-2 font-normal uppercase text-[9px] tracking-[0.2em] border-r border-white/10 whitespace-nowrap min-w-[70px]">
                                <div className="flex flex-col gap-2">
                                  <SortToggle col="chapter" sortConfig={sortConfig} setSortConfig={setSortConfig} />
                                  <PremiumMultiSelect
                                    label="CHAPTER"
                                    options={filterOptions.chapters}
                                    selected={filterChapter}
                                    isExclude={excludeChapter}
                                    onExcludeToggle={setExcludeChapter}
                                    onChange={(vals) => { setFilterChapter(vals); setPage(1); }}
                                  />
                                </div>
                              </th>
                              <th className="p-1 px-2 font-normal uppercase text-[9px] tracking-[0.2em] border-r border-white/10 whitespace-nowrap min-w-[70px]">
                                <div className="flex flex-col gap-2">
                                  <SortToggle col="region" sortConfig={sortConfig} setSortConfig={setSortConfig} />
                                  <PremiumMultiSelect
                                    label="REGION"
                                    options={filterOptions.regions}
                                    selected={filterRegion}
                                    isExclude={excludeRegion}
                                    onExcludeToggle={setExcludeRegion}
                                    onChange={(vals) => { setFilterRegion(vals); setPage(1); }}
                                  />
                                </div>
                              </th>
                              <th className="p-1 px-2 font-normal uppercase text-[9px] tracking-[0.2em] border-r border-white/10 whitespace-nowrap min-w-[70px]">
                                <div className="flex flex-col gap-2">
                                  <SortToggle col="locality" sortConfig={sortConfig} setSortConfig={setSortConfig} />
                                  <PremiumMultiSelect
                                    label="CITY/LOCALITY"
                                    plural="LOCALITIES"
                                    options={filterOptions.localities}
                                    selected={filterLocality}
                                    isExclude={excludeLocality}
                                    onExcludeToggle={setExcludeLocality}
                                    onChange={(vals) => { setFilterLocality(vals); setPage(1); }}
                                  />
                                </div>
                              </th>
                              <th className="p-1 px-2 font-normal uppercase text-[9px] tracking-[0.2em] border-r border-white/10 whitespace-nowrap min-w-[70px]">
                                <div className="flex flex-col gap-2">
                                  <SortToggle col="state" sortConfig={sortConfig} setSortConfig={setSortConfig} />
                                  <PremiumMultiSelect
                                    label="STATE"
                                    plural="STATES"
                                    options={filterOptions.states}
                                    selected={filterState}
                                    isExclude={excludeState}
                                    onExcludeToggle={setExcludeState}
                                    onChange={(vals) => { setFilterState(vals); setPage(1); }}
                                  />
                                </div>
                              </th>
                              <th className="p-1 px-2 font-normal uppercase text-[9px] tracking-[0.2em] border-r border-white/10 whitespace-nowrap min-w-[70px]">
                                <div className="flex flex-col gap-2">
                                  <SortToggle col="country" sortConfig={sortConfig} setSortConfig={setSortConfig} />
                                  <PremiumMultiSelect
                                    label="COUNTRY"
                                    plural="COUNTRIES"
                                    options={filterOptions.countries}
                                    selected={filterCountry}
                                    isExclude={excludeCountry}
                                    onExcludeToggle={setExcludeCountry}
                                    onChange={(vals) => { setFilterCountry(vals); setPage(1); }}
                                  />
                                </div>
                              </th>

                              <th className={cn(
                                "p-1 px-2 font-normal uppercase text-[9px] tracking-[0.2em] whitespace-nowrap min-w-[120px] text-center",
                                directoryType !== "membership" && "border-r border-white/10"
                              )}>
                                <div className="flex flex-col items-center gap-1">
                                  <Mail className="w-3 h-3 text-[#AE232D]/40" />
                                  <span className="text-[8px] opacity-40">EMAIL</span>
                                </div>
                              </th>
                              {directoryType !== "membership" && (
                                <th className="p-1 px-2 font-normal uppercase text-[9px] tracking-[0.2em] whitespace-nowrap min-w-[120px] text-center">
                                  <div className="flex flex-col items-center gap-1">
                                    <Phone className="w-3 h-3 text-[#AE232D]/40" />
                                    <span className="text-[8px] opacity-40">PHONE</span>
                                  </div>
                                </th>
                              )}
                            </tr>
                          </thead>
                          <tbody className={cn("divide-y divide-slate-50 transition-opacity duration-200", fetchingLeaders && "opacity-40 pointer-events-none")}>
                            {/* Always render rows when available — overlay opacity signals loading */}
                            {leaderData.length === 0 && !fetchingLeaders ? (
                              <tr>
                                <td colSpan={directoryType === "membership" ? 11 : 12} className="p-20 text-center text-slate-400 font-normal uppercase text-xs tracking-widest">No matching intelligence records found.</td>
                              </tr>
                            ) : leaderData.length === 0 && fetchingLeaders ? (
                              // Skeleton rows on first ever load (no previous data)
                              Array.from({ length: 8 }).map((_, i) => (
                                <tr key={i} className="border-b border-slate-50">
                                  {Array.from({ length: directoryType === "membership" ? 11 : 12 }).map((_, j) => (
                                    <td key={j} className="p-1 px-2">
                                      <div className="h-3 bg-slate-100 rounded animate-pulse" style={{ width: `${40 + Math.random() * 50}%`, animationDelay: `${(i * 12 + j) * 20}ms` }} />
                                    </td>
                                  ))}
                                </tr>
                              ))
                            ) : (
                              leaderData.map((leader, idx) => (
                                <tr
                                  key={`${leader.member_id}-${idx}`}
                                  className="group hover:bg-[#F8F9FA] transition-colors border-b border-slate-50"
                                >
                                  <td className="p-1 px-2">
                                    <div className="flex items-center gap-2">
                                      <div className={cn(
                                        "w-1.5 h-1.5 rounded-full shrink-0",
                                        leader.is_active ? "bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.4)]" : "bg-slate-300"
                                      )} title={leader.is_active ? "Active" : "Inactive"} />
                                      <span className="text-[11px] font-normal text-[#004D57] uppercase leading-tight">{leader.first_name} {leader.last_name}</span>
                                    </div>
                                  </td>
                                  <td className="p-1 px-2">
                                    <div className="flex flex-col items-start gap-1">
                                      <span className="text-[8px] font-normal px-2 py-1 bg-blue-50/50 text-blue-600/70 border border-blue-100/50 rounded uppercase tracking-widest leading-none">
                                        {ROLE_MAPPING[leader.program_journey] || leader.program_journey || "—"}
                                      </span>
                                      {leader.administration && leader.program_journey.toLowerCase().includes("candidate") && !leader.administration.toLowerCase().includes("june 2026") && !leader.administration.toLowerCase().includes("jun 2026") && !leader.administration.toLowerCase().includes("june 26") && (
                                        <span className="text-[6.5px] font-medium px-1.5 py-0.5 bg-amber-50 text-amber-600/90 border border-amber-200/50 rounded uppercase tracking-widest leading-none" title="Actual Administration">
                                          {leader.administration}
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                  <td className="p-1 px-2">
                                    <span className="text-[8px] font-normal px-2 py-1 bg-emerald-50/50 text-emerald-600/70 border border-emerald-100/50 rounded uppercase tracking-widest">{ROLE_MAPPING[leader.membership_journey] || leader.membership_journey || "—"}</span>
                                  </td>
                                  <td className="p-1 px-2">
                                    <span className="text-[11px] font-normal opacity-80 uppercase tracking-tight">{leader.cluster_center_name || "Unassigned"}</span>
                                  </td>
                                  <td className="p-1 px-2">
                                    <span className="text-[11px] font-normal opacity-80 uppercase tracking-tight">{leader.nearest_cluster_center_name || "Unassigned"}</span>
                                  </td>
                                  <td className="p-1 px-2">
                                    <span className="text-[11px] font-normal opacity-80 uppercase tracking-tight">{leader.chapter}</span>
                                  </td>
                                  <td className="p-1 px-2">
                                    <span className="text-[11px] font-normal opacity-80 uppercase tracking-tight">{leader.region}</span>
                                  </td>
                                  <td className="p-1 px-2">
                                    <div className="flex items-center gap-2">
                                      <MapPin className="w-2.5 h-2.5 text-[#A9C4BB]" />
                                      <span className="text-[11px] font-normal text-slate-600 uppercase leading-snug">
                                        {leader.city_name || "Unassigned"}
                                      </span>
                                    </div>
                                  </td>
                                  <td className="p-1 px-2">
                                    <span className="text-[11px] font-normal text-slate-500 uppercase tracking-tight">{leader.city_admin1 || "Unassigned"}</span>
                                  </td>
                                  <td className="p-1 px-2">
                                    <span className="text-[11px] font-normal text-slate-400 uppercase tracking-tight">{leader.city_country}</span>
                                  </td>
                                  <td className={cn("p-1 px-2", directoryType !== "membership" && "border-r border-slate-50")}>
                                    <div className="flex flex-col items-center justify-center">
                                      {leader.has_email ? (
                                        (activeRevealId === leader.member_id && activeContactData) ? (
                                          <div className="flex flex-col gap-1 items-center w-full max-w-[120px]">
                                            {activeContactData.emails.map((email: string, i: number) => (
                                              <button
                                                key={i}
                                                onClick={() => handleCopy(email, `${leader.member_id}-email-${i}`)}
                                                className="group/item flex items-center justify-between gap-2 w-full p-1 px-2 bg-slate-50 hover:bg-[#AE232D]/5 rounded border border-slate-100 transition-all"
                                              >
                                                <span className="text-[8px] font-mono lowercase truncate text-slate-600 group-hover/item:text-[#AE232D]">{email}</span>
                                                {copiedId === `${leader.member_id}-email-${i}` ? (
                                                  <Check className="w-2.5 h-2.5 text-emerald-500" />
                                                ) : (
                                                  <Copy className="w-2.5 h-2.5 text-slate-300 group-hover/item:text-[#AE232D]/40 opacity-0 group-hover/item:opacity-100 transition-opacity" />
                                                )}
                                              </button>
                                            ))}
                                          </div>
                                        ) : (
                                          <button
                                            onClick={() => handleReveal(leader.member_id)}
                                            disabled={isRevealing && activeRevealId === leader.member_id}
                                            className={cn(
                                              "p-1.5 px-3 rounded-lg hover:bg-[#AE232D]/10 transition-all group/reveal flex items-center gap-2",
                                              isRevealing && activeRevealId === leader.member_id && "animate-pulse"
                                            )}
                                          >
                                            <span className="text-[9px] font-mono lowercase text-[#AE232D]/40 group-hover/reveal:text-[#AE232D] transition-colors">{leader.masked_email}</span>
                                            <Mail className={cn(
                                              "w-3 h-3 text-[#AE232D]/60 group-hover/reveal:text-[#AE232D] transition-colors",
                                              isRevealing && activeRevealId === leader.member_id && "opacity-20"
                                            )} />
                                          </button>
                                        )
                                      ) : (
                                        <span className="text-slate-300 text-[10px]">—</span>
                                      )}
                                    </div>
                                  </td>
                                  {directoryType !== "membership" && (
                                    <td className="p-1 px-2">
                                      <div className="flex flex-col items-center justify-center">
                                        {leader.has_phone ? (
                                          (activeRevealId === leader.member_id && activeContactData) ? (
                                            <div className="flex flex-col gap-1 items-center w-full max-w-[120px]">
                                              {activeContactData.phones.map((phone: string, i: number) => (
                                                <button
                                                  key={i}
                                                  onClick={() => handleCopy(phone, `${leader.member_id}-phone-${i}`)}
                                                  className="group/item flex items-center justify-between gap-2 w-full p-1 px-2 bg-slate-50 hover:bg-[#AE232D]/5 rounded border border-slate-100 transition-all"
                                                >
                                                  <span className="text-[8px] font-mono lowercase truncate text-slate-600 group-hover/item:text-[#AE232D]">{phone}</span>
                                                  {copiedId === `${leader.member_id}-phone-${i}` ? (
                                                    <Check className="w-2.5 h-2.5 text-emerald-500" />
                                                  ) : (
                                                    <Copy className="w-2.5 h-2.5 text-slate-300 group-hover/item:text-[#AE232D]/40 opacity-0 group-hover/item:opacity-100 transition-opacity" />
                                                  )}
                                                </button>
                                              ))}
                                            </div>
                                          ) : (
                                            <button
                                              onClick={() => handleReveal(leader.member_id)}
                                              disabled={isRevealing && activeRevealId === leader.member_id}
                                              className={cn(
                                                "p-1.5 px-3 rounded-lg hover:bg-slate-100 transition-all group/reveal flex items-center gap-2",
                                                isRevealing && activeRevealId === leader.member_id && "animate-pulse"
                                              )}
                                            >
                                              <span className="text-[9px] font-mono text-slate-400 group-hover/reveal:text-[#004D57] transition-colors">{leader.masked_phone}</span>
                                              <Phone className={cn(
                                                "w-3 h-3 text-slate-400 group-hover/reveal:text-[#004D57] transition-colors",
                                                isRevealing && activeRevealId === leader.member_id && "opacity-20"
                                              )} />
                                            </button>
                                          )
                                        ) : (
                                          <span className="text-slate-300 text-[10px]">—</span>
                                        )}
                                      </div>
                                    </td>
                                  )}
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </DualScrollWrapper>

                    {/* Pagination Controls */}
                    <div className="px-6 py-5 bg-slate-50 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-4">
                      <span className="text-[10px] font-normal text-[#004D57] uppercase tracking-widest">
                        {totalLeaders.toLocaleString()} Total Records &nbsp;·&nbsp; Showing page {page} of {totalPages}
                      </span>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => setPage(p => Math.max(1, p - 1))}
                          disabled={page <= 1 || fetchingLeaders}
                          className="flex items-center gap-1.5 px-5 py-2.5 bg-white border border-slate-200 rounded-xl font-normal text-[9px] uppercase tracking-widest text-[#004D57] hover:bg-[#004D57] hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <ChevronLeft className="w-3 h-3" /> Prev
                        </button>
                        <span className="text-[10px] font-normal text-slate-400 tabular-nums">
                          {page} / {totalPages}
                        </span>
                        <button
                          onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                          disabled={page >= totalPages || fetchingLeaders}
                          className="flex items-center gap-1.5 px-5 py-2.5 bg-[#004D57] text-white rounded-xl font-normal text-[9px] uppercase tracking-widest hover:bg-[#005D69] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          Next <ChevronRight className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {isMembershipDirectoryBlocked && (
                    <div className="absolute inset-0 z-30 flex flex-col items-center justify-start pt-32 p-6 text-center bg-slate-50/20 backdrop-blur-[2px]">
                      <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="max-w-md bg-white/95 backdrop-blur-md rounded-3xl p-8 border border-[#004D57]/10 shadow-xl space-y-4"
                      >
                        <div className="w-12 h-12 bg-[#AE232D]/10 rounded-2xl flex items-center justify-center mx-auto text-[#AE232D]">
                          <Users className="w-6 h-6" />
                        </div>
                        <div className="space-y-2">
                          <h4 className="text-lg font-bold text-[#004D57] uppercase tracking-tight">Access Restricted</h4>
                          <p className="text-[#AE232D] text-[11px] font-bold uppercase tracking-wider leading-relaxed">
                            You are not an active dues-paying member so you do not have access to this section.
                          </p>
                        </div>
                        <p className="text-slate-400 font-normal uppercase tracking-widest text-[8px] leading-relaxed">
                          This directory is exclusively reserved for active CMT Charterholders, Professional Members, and Affiliate Members.
                        </p>
                      </motion.div>
                    </div>
                  )}
                </div>
              </motion.section>
            ) : (
              /* STANDARD DASHBOARD VIEW (FOOTPRINT / CHAPTERS) */
              <>
                {/* Visual Intel (Sunburst & Stats) */}
                <section className="space-y-4">
                  <div
                    className="flex-1 w-full bg-white rounded-2xl border border-slate-50 shadow-xl relative overflow-hidden group"
                    style={{ height: "580px" }}
                  >
                    <div className="absolute top-0 left-0 w-full h-24 bg-gradient-to-b from-[#F8F9FA]/50 to-transparent pointer-events-none"></div>
                    {/* Skeleton while chart data loads */}
                    {(activeView === "CHAPTER_HUB" ? loadingEligible || !eligibleData : !data) ? (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="relative w-72 h-72">
                          <div className="absolute inset-0 rounded-full border-[28px] border-slate-100 animate-pulse" />
                          <div className="absolute inset-8 rounded-full border-[20px] border-slate-50 animate-pulse" style={{ animationDelay: "150ms" }} />
                          <div className="absolute inset-[4.5rem] rounded-full bg-slate-50 animate-pulse" style={{ animationDelay: "300ms" }} />
                        </div>
                      </div>
                    ) : (
                      <ReactECharts
                        ref={chartRef}
                        option={getSunburstOption(
                          selectedRegion ? (
                            selectedChapter ? (
                              (activeView === "CHAPTER_HUB" ? eligibleData : data)?.cluster_sunbursts[selectedRegion]?.[selectedChapter] || []
                            ) : (
                              (activeView === "CHAPTER_HUB" ? eligibleData : data)?.regional_sunbursts[selectedRegion] || []
                            )
                          ) : (
                            (activeView === "CHAPTER_HUB" ? eligibleData : data)?.global_sunburst || []
                          ),
                          !!(selectedRegion || selectedChapter)
                        )}
                        notMerge={false}
                        style={{ height: "100%", width: "100%" }}
                        onEvents={{ click: onChartClick }}
                        lazyUpdate={true}
                      />
                    )}
                  </div>
                </section>
              </>
            )}
          </main>

          {/* 3. Right Sidebar: Legend (3/12) */}
          <aside className={cn(
            "hidden xl:flex xl:col-span-3 flex-col transition-all duration-300",
            (activeSubView === "DIRECTORY") && "xl:hidden"
          )}>
            <div className="bg-white/50 backdrop-blur-md rounded-2xl border border-white shadow-lg p-4 h-full min-h-[580px] flex flex-col">
              <h3 className="text-[10px] font-bold text-[#004D57] uppercase tracking-[0.2em] mb-3 flex items-center gap-2 border-b border-slate-100 pb-2">
                <div className="w-1.5 h-3 bg-[#AE232D] rounded-full" />
                Legend
              </h3>
              <div className="flex-1 overflow-y-auto pr-2 space-y-3 custom-scrollbar">
                {LEGEND_ITEMS.map((item) => (
                  <div
                    key={item.label}
                    className="flex flex-col p-3 rounded-xl border border-slate-100/50 bg-slate-50/50 shadow-sm group transition-all duration-300 hover:bg-white hover:shadow-md"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-3.5 h-3.5 rounded-full shrink-0 shadow-sm border border-white/60" style={{ backgroundColor: item.color }} />
                      <span className="text-[10px] font-bold text-slate-800 tracking-wider uppercase leading-tight">
                        {item.label}
                      </span>
                    </div>

                    <p className="text-[9.5px] text-slate-500 font-medium leading-relaxed pl-4 border-l border-[#004D57]/20 uppercase max-h-0 opacity-0 overflow-hidden transition-all duration-300 ease-in-out group-hover:max-h-24 group-hover:opacity-100 group-hover:mt-2">
                      {item.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </aside>
        </div>

        {/* Matrix Analysis Section (Full Width Below) */}
        {activeSubView !== "DIRECTORY" && tableConfig && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4 pt-4"
          >
            <div className="flex items-center justify-between px-4">
              <h2 className="text-2xl font-normal text-[#004D57] uppercase tracking-tighter flex items-center gap-4">
                <LayoutDashboard className="w-8 h-8 text-[#AE232D]" />
                {tableConfig.title}
              </h2>
            </div>

            {/* System Notices / Disclaimers */}
            {currentData?.notices && currentData.notices.length > 0 && (
              <div className="mx-4 p-4 bg-[#AE232D]/5 rounded-2xl border border-[#AE232D]/20 flex gap-3 items-start shadow-sm backdrop-blur-sm">
                <div className="p-1.5 bg-[#AE232D]/10 rounded-xl text-[#AE232D] shrink-0 mt-0.5 animate-pulse">
                  <AlertTriangle className="w-4 h-4" />
                </div>
                <div className="space-y-1">
                  <h5 className="text-[10px] font-black uppercase tracking-[0.2em] text-[#AE232D]">Important Data Notice</h5>
                  {currentData.notices.map((notice, idx) => (
                    <p key={idx} className="text-[11px] font-semibold text-slate-700 leading-relaxed uppercase tracking-wider">
                      {notice}
                    </p>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-white rounded-[2.5rem] overflow-hidden border border-slate-50 shadow-2xl p-2 relative">
              <div className={cn("transition-all duration-300", !hasStatsAccess && "blur-[6px] opacity-40 pointer-events-none select-none")}>
                <DualScrollWrapper>
                  <table className="w-full border-collapse">
                    <thead>
                      <tr>
                        <th className="bg-[#004D57] text-[#A9C4BB] p-2 text-left font-normal uppercase text-[9px] tracking-[0.2em] border-r border-white/5 sticky left-0 z-10 w-44 shadow-xl">Role Classification</th>
                        <th className="bg-[#AE232D] text-white p-2 text-center font-normal uppercase text-[9px] tracking-[0.2em] shadow-xl whitespace-nowrap">Total</th>
                        {tableConfig.columns.map(c => {
                          const isPartial = activeView === "CHAPTER_HUB" && (currentData?.chapter_eligibility?.[c] === "partial" || currentData?.chapter_eligibility?.[c] === "Partial");
                          const isProposed = activeView === "CHAPTER_HUB" && currentData?.chapter_eligibility?.[c] === "Proposed";
                          return (
                            <th key={c} className="bg-slate-50/80 text-[#004D57] p-2 text-center font-normal uppercase text-[9px] tracking-widest border-b border-slate-100 min-w-[90px]">
                              <div className="flex flex-col items-center gap-2">
                                <span>{c}</span>
                                {activeView === "CHAPTER_HUB" && (
                                  <button
                                    onClick={() => {
                                      setActiveSubView("DIRECTORY");
                                      if (selectedChapter) {
                                        setFilterRegion(selectedRegion ? [selectedRegion] : []);
                                        setFilterChapter([selectedChapter]);
                                        setFilterCluster([c]);
                                      } else if (selectedRegion) {
                                        setFilterRegion([selectedRegion]);
                                        setFilterChapter([c]);
                                        setFilterCluster([]);
                                      } else {
                                        setFilterRegion([c]);
                                        setFilterChapter([]);
                                        setFilterCluster([]);
                                      }
                                      setPage(1);
                                    }}
                                    className="flex items-center gap-1.5 px-3 py-1 rounded-lg bg-[#004D57]/5 text-[#004D57]/60 hover:bg-[#AE232D] hover:text-white transition-all text-[8px] font-normal uppercase tracking-widest group/btn"
                                  >
                                    <Users className="w-2.5 h-2.5 opacity-50 group-hover/btn:opacity-100" />
                                    Directory
                                  </button>
                                )}
                                {isPartial && (
                                  <span className="px-3 py-1 rounded-lg bg-amber-50 text-amber-700 text-[9px] font-normal border border-amber-100 shadow-sm animate-pulse">PARTIAL</span>
                                )}
                                {isProposed && (
                                  <span className="px-3 py-1 rounded-lg bg-blue-50 text-blue-700 text-[9px] font-normal border border-blue-100 shadow-sm animate-pulse">PROPOSED</span>
                                )}
                                {activeView === "CHAPTER_HUB" && currentData?.chapter_eligibility?.[c] === "recommended" && (
                                  <span className="px-3 py-1 rounded-lg bg-emerald-50 text-emerald-700 text-[9px] font-normal border border-emerald-100 shadow-sm animate-pulse">RECOMMENDED</span>
                                )}
                              </div>
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      <AnimatePresence mode="popLayout">
                        {tableConfig.rows.map((row, i) => (
                          <motion.tr
                            key={row.role}
                            layout
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className={cn(
                              "group hover:bg-[#F8F9FA] transition-all border-b border-slate-50",
                              i % 2 === 0 ? "bg-white" : "bg-slate-50/30"
                            )}
                          >
                            <td className="p-2 text-[11px] font-normal text-[#004D57] sticky left-0 bg-inherit backdrop-blur-md z-10 group-hover:bg-[#F8F9FA] shadow-sm uppercase tracking-tight">
                              {row.role}
                            </td>
                            <td className="p-2 text-center font-normal text-[11px] text-[#AE232D] bg-[#AE232D]/5 group-hover:bg-[#AE232D]/10 transition-colors">
                              {(row.Total || 0).toLocaleString()}
                            </td>
                            {tableConfig.columns.map(c => (
                              <td key={c} className="p-2 text-center text-[11px] font-normal text-slate-500 tabular-nums">
                                {((row[c] as number) || 0).toLocaleString()}
                              </td>
                            ))}
                          </motion.tr>
                        ))}
                      </AnimatePresence>
                    </tbody>
                  </table>
                </DualScrollWrapper>
              </div>

              {!hasStatsAccess && (
                <div className="absolute inset-0 z-30 flex flex-col items-center justify-start pt-32 p-6 text-center bg-slate-50/20 backdrop-blur-[2px]">
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="max-w-md bg-white/95 backdrop-blur-md rounded-3xl p-8 border border-[#004D57]/10 shadow-xl space-y-4"
                  >
                    <div className="w-12 h-12 bg-[#AE232D]/10 rounded-2xl flex items-center justify-center mx-auto text-[#AE232D]">
                      <Users className="w-6 h-6" />
                    </div>
                    <div className="space-y-2">
                      <h4 className="text-lg font-bold text-[#004D57] uppercase tracking-tight">Granular Statistics Restricted</h4>
                      <p className="text-slate-500 text-[10px] font-bold uppercase tracking-wider leading-relaxed">
                        Access to the detailed geographic assignment matrices is restricted to active volunteers currently assigned in the CMT Global Registry.
                      </p>
                    </div>
                    <p className="text-slate-400 font-normal uppercase tracking-widest text-[8px] leading-relaxed">
                      Please contact your Chapter Development Committee liaison or Staff Administrator if you need to be registered as an active chapter officer.
                    </p>
                  </motion.div>
                </div>
              )}


            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}

export default function DirectoryPage() {
  return <DirectoryContent />;
}
